"use client";

import React, { FormEvent, useCallback, useEffect, useMemo } from "react";
import styled, { useTheme } from "styled-components";
import {
  utils,
  SpaceThemeNames,
  useBoolean,
  ContextMenu,
  PopupMenu,
  Flex,
  useDrag,
  Box,
  Section,
  Kit,
  HeadingItem,
  buildURL,
  Text,
  Button,
  copy,
  useNavigation,
} from "@thenounproject/lingo-core";

import useShowModal, { ModalTypes } from "@redux/actions/useModals";
import useReorderHeading from "@redux/actions/sections/useReorderHeading";
import useNotifications from "@actions/useNotifications";
import { textItemDragStyle } from "@features/navigation/consts";
import useUpdateItem from "@actions/items/useUpdateItem";
import useDeleteHeadingWithItems from "@actions/items/useDeleteHeadingWIthItems";
import useMoveHeading from "@redux/actions/sections/useMoveHeading";
import useDuplicateHeading from "@redux/actions/sections/useDuplicateHeading";

import {
  AssetCopyTypes,
  SectionCallbackArgs,
} from "@features/content-management/modals/MoveCopyToSectionModal";
import useNavPoint from "@hooks/useNavPoint";

export type KitNavHeaderData = {
  header: HeadingItem;
  section: Section;
  kit: Kit;
  inView: boolean;
  canEdit: boolean;
  shareLink: string;
};

type HeaderWrapperProps = {
  inView: boolean;
  dragDirection?: "top" | "bottom";
  isHeadingDrag?: boolean;
};
const getHeaderWrapperThemeStyles = (props: HeaderWrapperProps & utils.ThemedProps) => {
  return (
    {
      [SpaceThemeNames.classic]: {
        borderLeft: `2px solid ${props.inView ? utils.getPrimaryColor(props) : "transparent"}`,
        pl: props.inView ? "8px" : "0px",
      },
      [SpaceThemeNames.scher]: {},
      [SpaceThemeNames.wyman]: {},
    }[props.theme.themeName] || {}
  );
};
const HeaderWrapper = styled(Flex).attrs<HeaderWrapperProps>(props => {
  return {
    transition: "all .3s ease",
    position: "relative",
    py: "5px",
    my: "-5px",
    ...getHeaderWrapperThemeStyles(props),
  };
})<HeaderWrapperProps>`
  overflow: visible;
  .overflow {
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  @media not (hover: none) {
    &:hover {
      .overflow {
        opacity: 1;
      }
    }
  }

  &:after {
    content: "";
    height: 2px;
    width: 100%;
    top: 0px;
    left: 0;
    background: ${props => utils.getPrimaryColor(props)};
    z-index: 10;
    position: absolute;
    opacity: 0;
  }

  &:before {
    content: "";
    height: 2px;
    width: 100%;
    bottom: 0px;
    left: 0;
    background: ${props => utils.getPrimaryColor(props)};
    z-index: 10;
    position: absolute;
    opacity: 0;
  }

  ${props =>
    props.dragDirection === "top" &&
    props.isHeadingDrag &&
    `
  &:after {
    opacity: 1;
  }
`};
  ${props =>
    props.dragDirection === "bottom" &&
    props.isHeadingDrag &&
    `
  &:before {
    opacity: 1;
  }
`};
`;

type HeadingButtonProps = {
  inView: boolean;
};
const HeadingButton = styled(Box).attrs<HeadingButtonProps>({
  as: "button",
  position: "relative",
  p: "none",
  textAlign: "left",
  width: "100%",
})<HeadingButtonProps>`
  * {
    color: black;
  }
`;

const KitNavHeaderItem: React.FC<KitNavHeaderData> = ({
  header,
  section,
  kit,
  inView,
  canEdit,
  shareLink,
}) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [editing, setEditing, setNotEditing] = useBoolean(false);
  const [menuOpen, setMenuOpen, setMenuClosed] = useBoolean(false);
  const [editValue, setEditValue] = React.useState(header.name);
  const [isDraggingOver, setIsDraggingOver] = React.useState(false);
  const { space, portal } = useNavPoint();
  const { showModal } = useShowModal();
  const { showNotification } = useNotifications();
  const navigation = useNavigation();
  const [reorderHeading] = useReorderHeading();

  const { themeName, primaryColor } = useTheme();
  const [deleteHeadingWithItems] = useDeleteHeadingWithItems();
  const [updateItem] = useUpdateItem();
  const [moveHeading] = useMoveHeading();
  const [duplicateHeading] = useDuplicateHeading();
  const [dragType, setDragType] = React.useState<"section" | "gallery" | "heading" | null>(null);

  const isHeadingDrag = dragType === "heading";

  useEffect(() => {
    if (header.name) setEditValue(header.name);
  }, [header.name]);

  useEffect(() => {
    if (editing) {
      inputRef.current.select();
    }
  }, [editing]);

  const {
    label,
    id,
    sectionId,
    // kitId
  } = useMemo(() => {
    return {
      label: header.name,
      id: header.uuid,
      sectionId: section.uuid,
      kitId: kit.kitUuid,
    };
  }, [header, section, kit]);

  /**
   * Header action handlers
   */

  const onEditHeader = useCallback(() => {
    return updateItem({ itemId: id, updates: { data: { content: editValue } } });
  }, [editValue, id, updateItem]);

  const onFinishEditing = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      const { error } = await onEditHeader();
      if (!error) {
        setNotEditing();
      }
    },
    [onEditHeader, setNotEditing]
  );

  const onDeleteHeader = useCallback(() => {
    showModal(ModalTypes.CONFIRMATION, {
      title: "Delete Heading",
      message: "Are you sure you want to delete this heading and all of its items?",
      buttonText: "Delete",
      buttonProcessingText: "Deleting...",
      onConfirm: async () => {
        return await deleteHeadingWithItems({ itemId: id });
      },
    });
  }, [deleteHeadingWithItems, id, showModal]);

  const onClickHeader = useCallback(() => {
    const queryParams = {
      kit_token: kit.shareToken || undefined,
      v: section.version,
    };
    const url = buildURL(`/s/${section.shortId}/`, { space, portal }, queryParams, header.shortId);
    navigation.push(url);
  }, [kit.shareToken, section.version, section.shortId, space, portal, header.shortId, navigation]);

  const onDoubleClickHeader = useCallback(() => {
    if (canEdit) {
      setEditing();
    }
  }, [canEdit, setEditing]);

  /**
   * Drag and drop handlers
   */

  const doOnDragStart = (e: React.DragEvent<HTMLDivElement>) => {
    /** Normalize browser UI during drag */
    e.dataTransfer.effectAllowed = "all";
    /** Create or reuse the previous drag image clone */
    const elem: HTMLElement = document.createElement("div");
    elem.setAttribute("style", textItemDragStyle);
    elem.innerHTML = label;
    document.body.appendChild(elem);
    e.dataTransfer.setDragImage(elem, 0, 0);
    e.dataTransfer.setData("navHeadingId", id);
    e.dataTransfer.setData("fromSectionId", sectionId);
    setTimeout(() => {
      document.body.removeChild(elem);
    }, 0);
  };

  const doOnDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e?.preventDefault();
    setIsDraggingOver(false);
    const headingToMove = e.dataTransfer.getData("navHeadingId");

    if (headingToMove === id) return;
    if (!headingToMove) return;

    const res = await reorderHeading({
      sectionId: sectionId,
      headingId: headingToMove,
      displayOrder: `${d.dragDirection.vertical === "top" ? "before" : "after"}:${id}`,
    });
    if (res.error) {
      showNotification({ message: res.error.message, level: "error" });
    }
  };

  const doOnDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    if (!isDraggingOver) {
      setIsDraggingOver(true);

      // Determine what's being dragged by checking dataTransfer types
      const types = Array.from(e.dataTransfer.types).map(t => t.toLowerCase());
      if (types.includes("reorderitemids")) {
        setDragType("gallery");
      } else if (types.includes("navheadingid")) {
        setDragType("heading");
      } else if (types.includes("sectionid")) {
        setDragType("section");
      }
    }
  };

  const doOnDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    // Only clear dragging state if we're actually leaving this element,
    // not just moving between child elements
    const relatedTarget = e.relatedTarget as Node | null;
    if (!relatedTarget || !ref.current?.contains(relatedTarget)) {
      setIsDraggingOver(false);
      setDragType(null);
    }
  };

  const doOnDragEnd = () => {
    setIsDraggingOver(false);
    setDragType(null);
  };

  const d = useDrag<HTMLDivElement>({
    dragRef: ref,
    doOnDragStart,
    doOnDragOver,
    doOnDragLeave,
    doOnDragEnd,
    doOnDrop,
  });
  const dragProps = canEdit ? d.dragProps : {};

  const moveHeader = useCallback(
    async ({ fromSectionId, selectedSection, selectedKit }: SectionCallbackArgs) => {
      const res = await moveHeading({
        headingId: header.uuid,
        fromSectionId,
        toSectionId: selectedSection.uuid,
      });
      if (res.isSuccess) {
        showNotification({
          message: `Heading moved to`,
          link: {
            text: selectedSection?.name || "Untitled Section",
            url: buildURL(`/s/${selectedSection?.shortId}`, {
              space,
              portal: selectedKit.kitUuid === kit.kitUuid ? portal : undefined,
            }),
          },
        });
      }
    },
    [header.uuid, kit.kitUuid, moveHeading, portal, showNotification, space]
  );
  const onMove = useCallback(() => {
    showModal(ModalTypes.MOVE_COPY_TO_SECTION, {
      type: "move",
      fromSectionId: section.uuid,
      callback: moveHeader,
      entityText: header.name,
    });
  }, [header.name, moveHeader, section.uuid, showModal]);

  const copyHeader = useCallback(
    async ({ selectedSection, copyAs, selectedKit }: SectionCallbackArgs) => {
      const res = await duplicateHeading({
        headingId: header.uuid,
        toSectionId: selectedSection.uuid,
        createReferences: copyAs === AssetCopyTypes.reference,
      });
      if (res.isSuccess) {
        showNotification({
          message: `Heading copied to`,
          link: {
            text: selectedSection?.name || "Untitled Section",
            url: buildURL(`/s/${selectedSection?.shortId}`, {
              space,
              portal: selectedKit.kitUuid === kit.kitUuid ? portal : undefined,
            }),
          },
        });
      }
    },
    [duplicateHeading, header.uuid, kit.kitUuid, portal, showNotification, space]
  );
  const onCopy = useCallback(() => {
    showModal(ModalTypes.MOVE_COPY_TO_SECTION, {
      type: "copy",
      fromSectionId: section.uuid,
      callback: copyHeader,
      entityText: header.name,
    });
  }, [copyHeader, header.name, section.uuid, showModal]);

  /**
   * Rendering funcs
   */
  function renderContextMenu() {
    const contextMenuItems = [];
    if (!editing) {
      if (shareLink) {
        contextMenuItems.push(
          <PopupMenu.Section key="share-link">
            <PopupMenu.Item onClick={() => copy(shareLink)} title="Copy share link" />
          </PopupMenu.Section>
        );
      }
      if (canEdit) {
        contextMenuItems.push(
          <PopupMenu.Section key="update-items">
            <PopupMenu.Item key="edit-item" onClick={setEditing} title="Rename heading" />
            <PopupMenu.Item onClick={onMove} title="Move to..." />
            <PopupMenu.Item onClick={onCopy} title="Copy to..." />
          </PopupMenu.Section>
        );

        contextMenuItems.push(
          <PopupMenu.Section key="edit-items">
            <PopupMenu.Item
              key="delete-item"
              onClick={onDeleteHeader}
              title="Delete heading & items"
            />
          </PopupMenu.Section>
        );
      }
    }

    if (contextMenuItems.length === 0) return;
    return (
      <>
        {/* Context menu for right click */}
        <ContextMenu data-testid="nav-context-menu" id={id} parentRef={ref}>
          {contextMenuItems}
        </ContextMenu>
        {/* Context menu for overflow button */}
        {menuOpen && (
          <PopupMenu source={`${id}-overflow-button`} close={setMenuClosed}>
            {contextMenuItems}
          </PopupMenu>
        )}
      </>
    );
  }

  const renderHeaderInput = () => {
    if (editing) {
      return (
        <Flex as="form" onSubmit={onFinishEditing}>
          <input
            ref={inputRef}
            style={{ padding: 0 }}
            type="text"
            value={editValue}
            onBlur={onFinishEditing}
            onChange={e => setEditValue(e.target.value)}
          />
        </Flex>
      );
    }
  };

  const { getColor } = utils;

  const renderHeaderButton = () => {
    const themedTextProps =
      {
        [SpaceThemeNames.scher]: {
          color: inView ? primaryColor : getColor("grayDarkest"),
        },
        [SpaceThemeNames.wyman]: {
          color: inView ? "black" : getColor("grayDarker"),
        },
      }[themeName] || {};

    if (!editing) {
      return (
        <HeadingButton inView={inView} onClick={onClickHeader} onDoubleClick={onDoubleClickHeader}>
          <Text as="span" transition="color .3s ease" {...themedTextProps}>
            <Box
              style={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                paddingRight: "14px",
              }}>
              {label}
            </Box>
          </Text>
        </HeadingButton>
      );
    }
  };

  return (
    <>
      {renderContextMenu()}
      <HeaderWrapper
        ref={ref}
        inView={inView}
        dragDirection={isDraggingOver ? d?.dragDirection?.vertical : undefined}
        isHeadingDrag={isHeadingDrag}
        {...dragProps}>
        <>
          {renderHeaderInput()}
          {renderHeaderButton()}
        </>
        {!editing && (
          <Button
            className="overflow"
            icon="navigation.overflow"
            size="small"
            p="none"
            position="absolute"
            right={0}
            top={2}
            width={21}
            height={21}
            themeOverrides={{
              primaryColor: "transparent",
              primaryColorDark: "transparent",
              primaryColorTint: "black",
            }}
            data-popup-source={`${id}-overflow-button`}
            onClick={e => {
              e.stopPropagation();
              setMenuOpen();
            }}
          />
        )}
      </HeaderWrapper>
    </>
  );
};

export default React.memo(KitNavHeaderItem);
